/**
 * Pure helpers behind the full-screen category and account pickers.
 *
 * Zero React, zero DOM, zero network — the colour derivation and the
 * most-frequent ranking are the parts worth pinning down in a unit test, and
 * the pickers themselves stay presentational.
 */

import type { Category } from "@/components/categories/categoryHelpers";
import type { SuggestionTemplate } from "@/lib/suggestions/service";

/**
 * Colours used when a Category or Account carries no `color` of its own.
 * Deliberately a fixed list rather than an HSL computation: these are picked
 * to read against both the light and dark `--cp-surface`, and a hash into a
 * curated list can never land on an unreadable near-white.
 */
export const PICKER_PALETTE = [
  "#f97316", // orange  — Food & Drinks in the reference
  "#3b82f6", // blue    — Shopping
  "#f59e0b", // amber   — Housing
  "#6b7280", // grey    — Transportation
  "#a855f7", // purple  — Vehicle
  "#22c55e", // green   — Life & Entertainment
  "#06b6d4", // cyan    — Communication
  "#ec4899", // pink
  "#14b8a6", // teal
  "#ef4444", // red
] as const;

/**
 * FNV-1a over the seed, so the same category always gets the same colour —
 * across renders, sessions and devices. Never `Math.random()`: a colour that
 * changes on re-render destroys the "shape recognition" the reference design
 * relies on.
 */
export function stableColorFor(seed: string): string {
  let hash = 0x811c9dc5;
  for (let i = 0; i < seed.length; i++) {
    hash ^= seed.charCodeAt(i);
    hash = Math.imul(hash, 0x01000193) >>> 0;
  }
  return PICKER_PALETTE[hash % PICKER_PALETTE.length];
}

interface ColorableCategory {
  id: string;
  color?: string | null;
}

/** The stored colour when there is one, otherwise a stable derived colour. */
export function categoryColor(category: ColorableCategory): string {
  return category.color || stableColorFor(category.id);
}

/**
 * Children inherit the parent's colour — in the reference every Shopping
 * subcategory is the same blue, which is what makes a long list scannable.
 * A child's own stored colour is deliberately ignored.
 */
export function childColor(parent: ColorableCategory, _child: ColorableCategory): string {
  void _child;
  return categoryColor(parent);
}

/**
 * The archived rule, verbatim from CLAUDE.md: archiving is a selection
 * concern, never a delete. An archived category is hidden from the list
 * *except* when it is the one currently assigned to the record being edited,
 * so that record can still show what it is set to.
 */
export function selectableCategories(categories: Category[], value: string | null): Category[] {
  return categories.filter((c) => !c.isArchived || c.id === value);
}

/**
 * Flat, case-insensitive name search. Flat is the point: search results are a
 * plain list of matches, not a tree — pushing a drill-down screen from a
 * search hit would lose the query.
 */
export function searchCategories(categories: Category[], query: string): Category[] {
  const q = query.trim().toLowerCase();
  if (!q) return categories;
  return categories.filter((c) => c.name.toLowerCase().includes(q));
}

/**
 * The `MOST FREQUENT` band, derived from the history-based suggestions the
 * app already computes (own-data, last 180 days, ranked frequency-then-
 * recency). Suggestion order is preserved — the service already did the
 * ranking, and re-sorting here would silently diverge from the templates
 * strip on the entry screen.
 *
 * Returns [] when there is no usable history, so the caller renders no band
 * at all rather than an empty one.
 */
export function topFrequentCategories(
  suggestions: SuggestionTemplate[],
  categories: Category[],
  limit = 4,
): Category[] {
  const byId = new Map(categories.map((c) => [c.id, c] as const));
  const out: Category[] = [];
  const seen = new Set<string>();
  for (const s of suggestions) {
    if (seen.has(s.categoryId)) continue;
    const category = byId.get(s.categoryId);
    // A suggestion can outlive its category being archived or deleted.
    if (!category || category.isArchived) continue;
    seen.add(s.categoryId);
    out.push(category);
    if (out.length >= limit) break;
  }
  return out;
}

/* --------------------------------------------------------------------------
   Accounts — same row anatomy as categories, so the two pickers read as one
   system rather than two unrelated screens.
   -------------------------------------------------------------------------- */

export interface PickerAccount {
  id: string;
  bankName: string;
  nickname?: string | null;
  accountType?: string | null;
  color?: string | null;
}

const ACCOUNT_TYPE_LABEL: Record<string, string> = {
  SAVINGS: "Savings",
  CURRENT: "Current",
  FD: "Fixed deposit",
  RD: "Recurring deposit",
  CREDIT_CARD: "Credit card",
};

/** The nickname is what the user actually calls the account; bank is backup. */
export function accountPrimaryLabel(account: PickerAccount): string {
  return account.nickname?.trim() || account.bankName;
}

/**
 * Bank + type, minus whatever the primary line already said. The reference
 * puts a balance here; AIM's account balance is compute-on-read and not part
 * of `GET /api/accounts`, so fetching it per row would mean an N+1 on picker
 * open. Bank and type identify the account without that cost.
 */
export function accountSecondaryLabel(account: PickerAccount): string {
  const type = account.accountType ? ACCOUNT_TYPE_LABEL[account.accountType] ?? account.accountType : "";
  const showBank = accountPrimaryLabel(account) !== account.bankName;
  return [showBank ? account.bankName : "", type].filter(Boolean).join(" · ");
}

export function accountColor(account: PickerAccount): string {
  return account.color || stableColorFor(account.id);
}
